import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projects as projectsApi, clients as clientsApi, nps as npsApi } from '@/utils/api';
import { ArrowLeft, Settings2, Calendar as CalIcon, Check, X as XIcon, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

// Tabs
import VaultTab from '@/components/project/VaultTab';
import DocumentsTab from '@/components/project/DocumentsTab';
import BugsTab from '@/components/project/BugsTab';
import ChatTab from '@/components/project/ChatTab';
import EstimatesTab from '@/components/project/EstimatesTab';
import InvoicesTab from '@/components/project/InvoicesTab';
import AIBriefTab from '@/components/project/AIBriefTab';
import TimeTab from '@/components/project/TimeTab';
import SowsTab from '@/components/project/SowsTab';
import RecurringInvoicesTab from '@/components/project/RecurringInvoicesTab';
import ChangeRequestsTab from '@/components/project/ChangeRequestsTab';
import ZoomMeetings from '@/pages/operator/ZoomMeetings';
import RequestFeedbackModal from '@/components/project/RequestFeedbackModal';

const CLOSING_STATUSES = new Set(['Delivered', 'Archived']);

const STATUS_COLORS = {
  'New': '#5C6B72', 'Scoping': '#006080', 'Estimate Sent': '#D9A441',
  'Approved': '#3C8A5A', 'In Progress': '#006080', 'UAT': '#0066CC',
  'Delivered': '#3C8A5A', 'Archived': '#8A99A0',
};

const ALL_STATUSES = ['New', 'Scoping', 'Estimate Sent', 'Approved', 'In Progress', 'UAT', 'Delivered', 'Archived'];

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'ai', label: 'AI Brief' },
  { id: 'vault', label: 'Vault' },
  { id: 'documents', label: 'Documents' },
  { id: 'bugs', label: 'Bugs' },
  { id: 'chat', label: 'Chat' },
  { id: 'estimates', label: 'Estimates' },
  { id: 'sows', label: 'SOW' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'recurring', label: 'Recurring' },
  { id: 'time', label: 'Time' },
  { id: 'calls', label: 'Calls' },
  { id: 'changes', label: 'Changes' },
];

function OverviewTab({ project, onStatusChange, onUpdate }) {
  const [status, setStatus] = useState(project.status);
  const [saving, setSaving] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState(null);

  // Load existing feedback status (so we can show a persistent button)
  useEffect(() => {
    if (!project?.id) return;
    if (!CLOSING_STATUSES.has(project.status)) { setFeedbackStatus(null); return; }
    npsApi.status(project.id).then(r => setFeedbackStatus(r.data)).catch(() => {});
  }, [project?.id, project?.status]);

  const handleStatus = async (newStatus) => {
    setSaving(true);
    try {
      await projectsApi.updateStatus(project.id, newStatus);
      const prevStatus = status;
      setStatus(newStatus);
      onStatusChange(newStatus);
      toast.success('Status updated');
      // Pop the feedback modal only on transitions INTO a closing status
      if (CLOSING_STATUSES.has(newStatus) && !CLOSING_STATUSES.has(prevStatus)) {
        setFeedbackModal(true);
      }
    } catch { toast.error('Failed to update status'); }
    finally { setSaving(false); }
  };

  const refreshFeedbackStatus = () => {
    if (project?.id && CLOSING_STATUSES.has(status)) {
      npsApi.status(project.id).then(r => setFeedbackStatus(r.data)).catch(() => {});
    }
  };

  const showRequestButton = CLOSING_STATUSES.has(status) && feedbackStatus && !feedbackStatus.is_answered;

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Status control */}
      <div className="card-base p-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Project Status</h3>
          {showRequestButton && (
            <button
              onClick={() => setFeedbackModal(true)}
              className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1.5"
              style={{ background: '#5B2D8E15', color: '#5B2D8E' }}
              data-testid="request-feedback-btn"
              title={feedbackStatus?.is_sent ? 'Feedback request pending — click to view' : 'Send the client an NPS + metrics + benefits feedback request'}
            >
              <Sparkles size={11} />
              {feedbackStatus?.is_sent ? 'Feedback pending…' : 'Request feedback'}
            </button>
          )}
          {feedbackStatus?.is_answered && (
            <span className="text-xs px-2.5 py-1 rounded-btn font-semibold flex items-center gap-1" style={{ background: '#3C8A5A15', color: '#3C8A5A' }} data-testid="feedback-answered-badge">
              <Check size={11} /> Feedback received · {feedbackStatus.answered.score}/10
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {ALL_STATUSES.map(s => (
            <button
              key={s}
              onClick={() => handleStatus(s)}
              disabled={saving}
              className="text-xs px-3 py-1.5 rounded-full font-semibold transition-all"
              style={{
                background: s === status ? `${STATUS_COLORS[s]}` : `${STATUS_COLORS[s]}15`,
                color: s === status ? 'white' : STATUS_COLORS[s],
              }}
              data-testid={`status-btn-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Details */}
      <div className="card-base p-4 grid grid-cols-2 gap-4">
        {[
          { label: 'Project Type', value: project.project_type },
          { label: 'Budget Band', value: project.budget_band || 'Not set' },
          { label: 'Timeline', value: project.timeline || 'Not set' },
          { label: 'Planned Delivery', value: <PlannedDeliveryEditor project={project} onUpdate={onUpdate} /> },
          { label: 'Created', value: new Date(project.created_at).toLocaleDateString() },
        ].map(({ label, value }) => (
          <div key={label}>
            <p className="text-xs font-semibold uppercase tracking-wide mb-0.5" style={{ color: 'var(--muted)' }}>{label}</p>
            <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Description */}
      {project.description && (
        <div className="card-base p-4">
          <h3 className="font-bold text-sm mb-2" style={{ color: 'var(--ink)' }}>Description</h3>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--muted)' }}>{project.description}</p>
        </div>
      )}

      {/* Milestones */}
      {project.milestones?.length > 0 && (
        <div className="card-base p-4">
          <h3 className="font-bold text-sm mb-3" style={{ color: 'var(--ink)' }}>Milestones</h3>
          <div className="space-y-2">
            {project.milestones.map((m, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b last:border-0" style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ background: m.status === 'completed' ? '#3C8A5A' : m.status === 'in_progress' ? '#006080' : '#E8E3D9' }}
                  />
                  <span className="text-sm" style={{ color: 'var(--ink)' }}>{m.name}</span>
                </div>
                <span className="text-xs" style={{ color: 'var(--muted)' }}>{m.due_date}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <RequestFeedbackModal
        open={feedbackModal}
        onClose={() => { setFeedbackModal(false); refreshFeedbackStatus(); }}
        project={project}
        onSent={() => refreshFeedbackStatus()}
      />
    </div>
  );
}

function PlannedDeliveryEditor({ project, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(project.planned_delivery_date || '');
  const save = async () => {
    try {
      await projectsApi.update(project.id, { planned_delivery_date: value || null });
      toast.success('Delivery date updated');
      setEditing(false);
      onUpdate && onUpdate();
    } catch { toast.error('Update failed'); }
  };
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <input type="date" className="input-agency text-xs py-1 px-2" value={value} onChange={e => setValue(e.target.value)} data-testid="planned-delivery-date-input" />
        <button onClick={save} className="p-1" style={{ color: '#3C8A5A' }} data-testid="planned-delivery-save"><Check size={13} /></button>
        <button onClick={() => { setEditing(false); setValue(project.planned_delivery_date || ''); }} className="p-1" style={{ color: 'var(--muted)' }}><XIcon size={13} /></button>
      </span>
    );
  }
  return (
    <button className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: 'var(--ink)' }} onClick={() => setEditing(true)} data-testid="edit-planned-delivery">
      {project.planned_delivery_date
        ? <>{new Date(project.planned_delivery_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</>
        : <span style={{ color: 'var(--muted)' }}>Not set</span>}
      <CalIcon size={11} style={{ color: 'var(--muted)' }} />
    </button>
  );
}

export default function ProjectDetail() {
  const { clientId, projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchProject = () => projectsApi.get(projectId).then(r => setProject(r.data)).catch(() => {});

  useEffect(() => {
    Promise.all([
      projectsApi.get(projectId),
      clientsApi.get(clientId),
    ]).then(([p, c]) => {
      setProject(p.data);
      setClient(c.data);
    }).catch(() => {});
  }, [projectId, clientId]);

  if (!project) return (
    <div className="p-6">
      <div className="h-6 w-48 rounded animate-pulse mb-2" style={{ background: 'var(--border)' }} />
      <div className="h-24 rounded-card animate-pulse" style={{ background: 'var(--border)' }} />
    </div>
  );

  const statusColor = STATUS_COLORS[project.status] || '#006080';

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 px-6 pt-5 pb-0" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <button
          className="flex items-center gap-1.5 text-xs mb-3 transition-colors hover:opacity-70"
          style={{ color: 'var(--muted)' }}
          onClick={() => navigate(`/admin/clients/${clientId}`)}
          data-testid="back-to-client"
        >
          <ArrowLeft size={12} /> {client?.company_name || 'Client'}
        </button>

        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h1 className="text-xl font-black" style={{ color: 'var(--ink)' }}>{project.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span
                className="badge-status"
                style={{ background: `${statusColor}15`, color: statusColor }}
                data-testid="project-status-badge"
              >
                {project.status}
              </span>
              <span className="text-xs" style={{ color: 'var(--muted)' }}>{project.project_type}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 overflow-x-auto" data-testid="project-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="px-4 py-2.5 text-sm font-medium transition-colors border-b-2 whitespace-nowrap"
              style={{
                borderColor: activeTab === tab.id ? '#006080' : 'transparent',
                color: activeTab === tab.id ? '#006080' : 'var(--muted)',
                background: 'transparent',
              }}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'overview' && (
          <OverviewTab project={project} onStatusChange={s => setProject(p => ({ ...p, status: s }))} onUpdate={fetchProject} />
        )}
        {activeTab === 'ai' && <AIBriefTab project={project} clientId={clientId} />}
        {activeTab === 'vault' && <VaultTab projectId={projectId} isOperator={true} />}
        {activeTab === 'documents' && <DocumentsTab projectId={projectId} isOperator={true} />}
        {activeTab === 'bugs' && <BugsTab projectId={projectId} isOperator={true} />}
        {activeTab === 'chat' && <ChatTab projectId={projectId} isOperator={true} />}
        {activeTab === 'estimates' && <EstimatesTab projectId={projectId} project={project} isOperator={true} />}
        {activeTab === 'sows' && <SowsTab projectId={projectId} isOperator={true} />}
        {activeTab === 'invoices' && <InvoicesTab projectId={projectId} project={project} client={client} isOperator={true} />}
        {activeTab === 'recurring' && <RecurringInvoicesTab projectId={projectId} />}
        {activeTab === 'time' && <TimeTab projectId={projectId} />}
        {activeTab === 'calls' && <ZoomMeetings scopedProjectId={projectId} isOperator={true} />}
        {activeTab === 'changes' && <ChangeRequestsTab projectId={projectId} isOperator={true} />}
      </div>
    </div>
  );
}
